import { type PayloadAction, createSlice, nanoid } from '@reduxjs/toolkit';

import type { ReactionEvent } from '@/lib/realtime/events';
import type { RootState } from '../store';

/**
 * Message-reactions state (#8198/#2607), keyed by `chat_id` (the persisted
 * `messages.id` the reaction targets). Reactions are DB-backed: hydrated on load
 * via `reactionsLoaded`, updated live via `reactionReceived` (add, or drop on
 * `removed`). One entry per (user, emoji) per message, mirroring the DB
 * `UNIQUE(chat_id, user_id, emoji)`.
 *
 * `reactionsPruned` / `REACTION_TTL_MS` are retained for the (now-unused) TTL
 * path but reactions no longer auto-expire — they persist until un-reacted.
 */

/** Legacy TTL constant (reactions no longer auto-expire; kept for the pruner). */
export const REACTION_TTL_MS = 5000;

export interface StoredReaction {
  /** Client-side unique id so React keys are stable across prunes. */
  id: string;
  emoji: string;
  userId: string;
  /** Epoch ms the reaction was recorded — the basis for TTL expiry. */
  receivedAt: number;
}

interface ReactionsState {
  /** chat_id -> the reactions currently displayed on that message. */
  byChatId: Record<number, StoredReaction[]>;
}

const initialState: ReactionsState = {
  byChatId: {},
};

const reactionsSlice = createSlice({
  name: 'reactions',
  initialState,
  reducers: {
    /**
     * Record an incoming (or optimistic self) reaction, appending it to its
     * message's transient list. `receivedAt` defaults to now but can be
     * supplied (tests / replay) via the meta timestamp on the payload.
     */
    reactionReceived: {
      reducer: (
        state,
        action: PayloadAction<{ event: ReactionEvent; receivedAt: number }>
      ) => {
        const { event, receivedAt } = action.payload;
        const list = state.byChatId[event.chat_id] ?? [];
        if (event.removed) {
          // Toggle-off: drop this (user, emoji) reaction. Idempotent — a missing
          // one is a no-op (optimistic remove + the WS echo both land here).
          const kept = list.filter(
            (r) => !(r.userId === event.user_id && r.emoji === event.emoji)
          );
          if (kept.length === 0) {
            delete state.byChatId[event.chat_id];
          } else {
            state.byChatId[event.chat_id] = kept;
          }
          return;
        }
        // Add, deduped by (user, emoji): a reactor's optimistic self-echo AND
        // the WS broadcast of their own reaction both arrive here — without this
        // the reactor double-counts their own 👍. One reaction per user+emoji
        // per message (mirrors the DB UNIQUE(chat_id, user_id, emoji)).
        const already = list.some(
          (r) => r.userId === event.user_id && r.emoji === event.emoji
        );
        if (!already) {
          list.push({
            id: nanoid(),
            emoji: event.emoji,
            userId: event.user_id,
            receivedAt,
          });
          state.byChatId[event.chat_id] = list;
        }
      },
      prepare: (event: ReactionEvent) => ({
        payload: { event, receivedAt: Date.now() },
      }),
    },
    /**
     * Hydrate persisted reactions on session load (#2607) — replaces the store
     * for these chats so a reload/late-join renders the DB truth. Each item is
     * one persisted row: { chat_id, user_id, emoji }.
     */
    reactionsLoaded: {
      reducer: (
        state,
        action: PayloadAction<{
          reactions: { chat_id: number; user_id: string; emoji: string }[];
          receivedAt: number;
        }>
      ) => {
        const { reactions, receivedAt } = action.payload;
        const next: Record<number, StoredReaction[]> = {};
        for (const r of reactions) {
          const list = next[r.chat_id] ?? [];
          list.push({
            id: nanoid(),
            emoji: r.emoji,
            userId: r.user_id,
            receivedAt,
          });
          next[r.chat_id] = list;
        }
        state.byChatId = next;
      },
      prepare: (
        reactions: { chat_id: number; user_id: string; emoji: string }[]
      ) => ({ payload: { reactions, receivedAt: Date.now() } }),
    },
    /** Drop every reaction older than `REACTION_TTL_MS` relative to `now`. */
    reactionsPruned: (state, action: PayloadAction<number>) => {
      const now = action.payload;
      for (const chatId of Object.keys(state.byChatId)) {
        const key = Number(chatId);
        const list = state.byChatId[key] ?? [];
        const kept = list.filter((r) => now - r.receivedAt < REACTION_TTL_MS);
        if (kept.length === 0) {
          delete state.byChatId[key];
        } else {
          state.byChatId[key] = kept;
        }
      }
    },
    /** Clear all reactions (e.g. on leaving the session). */
    reactionsCleared: (state) => {
      state.byChatId = {};
    },
  },
});

export const {
  reactionReceived,
  reactionsLoaded,
  reactionsPruned,
  reactionsCleared,
} = reactionsSlice.actions;

const EMPTY: StoredReaction[] = [];

/** Reactions currently displayed on a given message (empty array if none). */
export const selectReactionsForChat =
  (chatId: number | undefined) =>
  (state: RootState): StoredReaction[] =>
    chatId == null ? EMPTY : (state.reactions.byChatId[chatId] ?? EMPTY);

/** True when any message in the session currently has a live reaction. */
export const selectHasAnyReactions = (state: RootState): boolean =>
  Object.keys(state.reactions.byChatId).length > 0;

export default reactionsSlice.reducer;
